import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import s from './SequenceVersion.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { initialSequenceVersionState } from '../../reducers/initialState';
import SequenceVersionTable from './SequenceVersionTable';
import Pagination from '../Pagination/Pagination';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import SearchBar from '../SearchBar/SearchBar';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import ModelConfigurationContentTemplate from '../ModelTab/ModelConfigurationContentTemplate';
import { DEFAULT_PAGE_SIZE, ModelCategoryPermission } from '../../constants';
import { getQuerystring } from '../../utils';
import Banner from '../Banner/Banner';
import { v4 as uuid } from 'uuid';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import { getModelCategoryPermission } from '../../utils';

const SequenceVersion = ({
  getSequenceVersions,
  setCurrentPage,
  setFilteredData,
  sequenceVersionManager = { ...initialSequenceVersionState.sequenceVersionManager },
  userProfileWidgets,
  redirect,
  currentTimezone,
  sequenceVersionCleanUp,
  saveSequenceVersion,
  changeSequenceVersion,
  userModelCategories,
  intl
}) => {
  const SEARCH_FILTER = 'SEARCH_FILTER';
  let { ModelCategoryId, ModelId } = useParams();
  const [searchFilter, setSearchFilter] = useState('');

  const changeAllowed = IsWidgetAccessible(userProfileWidgets, `SEQUENCEVERSIONUPDATE`);
  let id = getQuerystring('id') || '0';

  useEffect(() => {
    if (id == '0')
      getSequenceVersions(ModelCategoryId, ModelId);
  }, [getSequenceVersions, ModelCategoryId, ModelId]);


  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };

  const searchHandler = value => {
    setSearchFilter(value);
    filteredResults(SEARCH_FILTER, value);
    setCurrentPage(1);
  };

  const duplicateSequenceVersion = id => {
    let saveData = {};
    saveData.SequenceVersionId = id;
    saveData.ModelId = ModelId;
    saveData.IsCopy = true;
    saveData.ModelCategoryId = ModelCategoryId
    sequenceVersionManager.duplicateSequence = true;
    changeSequenceVersion(sequenceVersionManager);
    saveSequenceVersion(saveData, 'SEQUENCEVERSIONUPDATE');
  };

  useEffect(() => {
    return () => {
      sequenceVersionCleanUp(initialSequenceVersionState.sequenceVersionManager);
    };
  }, []);

  let sequenceVersionObj = Object.values(sequenceVersionManager.filteredResults);

  const filteredResults = (filterType, value) => {
    let filteredList = sequenceVersionManager.sequenceVersions;

    let apifilterObj = {
      filter: null
    };

    if (filterType === SEARCH_FILTER) {
      if (value !== '') {
        apifilterObj.filter = value;
        filteredList = filteredList.filter(function (item) {
          return item.Name.toLowerCase().includes(value.toLowerCase());
        });
      }
    } else {
      if (searchFilter !== '') {
        apifilterObj.filter = searchFilter;
        filteredList = filteredList.filter(function (item) {
          return item.Name.toLowerCase().includes(searchFilter.toLowerCase());
        });
      }
    }
    setFilteredData({ filteredResults: { filteredResults: filteredList } });
  };
  const returnTo = () => {
    if (id != '0') {
      return (
        <Navigate
          to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/sequence-version/${id}`}
        />
      );
    }
  };

  let messageId = (sequenceVersionManager && sequenceVersionManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });


  //To reaload the page after copy
  useEffect(() => {
    if (sequenceVersionManager.isOpSuccessful && sequenceVersionManager.showBanner && sequenceVersionManager.duplicateSequence) {
      getSequenceVersions(ModelCategoryId, ModelId);
    }
  }, [
    getSequenceVersions,
    ModelCategoryId,
    ModelId,
    sequenceVersionManager.isOpSuccessful,
    sequenceVersionManager.showBanner,
    sequenceVersionManager.duplicateSequence
  ]);

  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: <FormattedMessage id="model.ModelSequences" defaultMessage="Model Sequences" /> } // Last item has no href (current page)
  ];

  let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)

  return (
    <ModelConfigurationContentTemplate selectedPage="manageModelSequences" userProfileWidgets={userProfileWidgets} intl={intl}>
      {returnTo()}
      <Banner
        key={uuid()}
        failureText={messageText}
        showBanner={(sequenceVersionManager.showBanner && sequenceVersionManager.duplicateSequence) || false}
        status={sequenceVersionManager.isOpSuccessful}
        warning={sequenceVersionManager.warning}
        successText={messageText}
      />
      <div style={WidgetVisibility(userProfileWidgets, `SEQUENCEVERSIONLIST`)}>
        <div className={s.pageContent}>
          <div className={s.sequenceVersions}>
            {sequenceVersionManager.isLoading && <LoadingSpinner />}
            <div className={s.contentWrapper}>
              <div className={s.componentHeader}>
                <div className={s.subHeading}>
                  <FormattedMessage id="model.manageModelSequences" defaultMessage="Manage Model Sequences" />
                  <span className={s.circle}>
                    {sequenceVersionManager.sequenceVersions.length}
                  </span>
                </div>
              </div>
              <div className='pb-3'>
                <Breadcrumb items={breadcrumbItems} />
              </div>
              <Row className={s.topRow}>
                <Col sm={6} lg={4}>
                  <div>
                    <SearchBar
                      searchHandler={searchHandler}
                      clearSearchInVisible={true}
                      initialText={searchFilter}
                      placeHolderTextId="sequenceVersionManagement.search"
                    />
                  </div>
                </Col>
                <Col sm={6} lg={8} style={WidgetVisibility(userProfileWidgets, `SEQUENCEVERSIONADD`)}>
                  {isModelCategoryOwner && <Link
                    to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/sequence-version/0`}
                  >
                    <Button className={s.addbutton}>
                      + <FormattedMessage id="sequenceVersionManagement.addNew" defaultMessage="ADD NEW" />
                    </Button>
                  </Link>}
                </Col>
              </Row>
              <div className="">
                <Pagination
                  data={sequenceVersionObj}
                  pageSize={DEFAULT_PAGE_SIZE}
                  onPageChange={onPageChangeHandler}
                  startingPage={sequenceVersionManager.currentPage}
                >
                  <SequenceVersionTable
                    redirect={redirect}
                    currentPage={sequenceVersionManager.currentPage}
                    currentTimezone={currentTimezone}
                    modelId={ModelId}
                    modelCategoryId={ModelCategoryId}
                    changeAllowed={changeAllowed}
                    duplicateSequenceVersion={duplicateSequenceVersion}
                    isModelCategoryOwner={isModelCategoryOwner}
                  />
                </Pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModelConfigurationContentTemplate>
  );
};


SequenceVersion.propTypes = {
  setPageFilter: PropTypes.func.isRequired,
  getSequenceVersion: PropTypes.func.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  setPageFilter: PropTypes.func.isRequired,
  currentTimezone: PropTypes.string.isRequired,
  sequenceVersionManager: PropTypes.object.isRequired
};

export default injectIntl(SequenceVersion);
